import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Iconify from 'src/components/Iconify';
import { Button } from "src/@/components/ui/button";
import { Input } from 'src/@/components/ui/input';
import { Table, TableBody, TableHead, TableHeader, TableRow, TableCell } from 'src/@/components/ui/table';
import { Card } from 'src/@/components/ui/card';
import Scrollbar from 'src/components/Scrollbar';
import useSettings from 'src/hooks/useSettings';
import { PATH_DASHBOARD } from 'src/routes/paths';
import formulariosService from 'src/services/formulariosService';
import useAuth from 'src/hooks/useAuth';
import { useToast } from 'src/@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "src/@/components/ui/dialog";
import config from 'src/config';
import PageContainerHeader from 'src/components/PageContainerHeader';

export default function FormulariosPublicos() {
  const { themeStretch } = useSettings();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);

  useEffect(() => {
    if (user?.clienteId) {
      loadForms();
    }
  }, [user?.clienteId]);

  const loadForms = async () => {
    try {
      setLoading(true);
      const response = await formulariosService.listar(user?.clienteId);
      if (!response.error) {
        setForms(response.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar formulários:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar os formulários",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleNewForm = async () => {
    try {
      setCreatingDraft(true);
      const response = await formulariosService.criarDraft(user?.clienteId, user?.id);
      if (!response.error && response.data?._id) {
        navigate(`/dashboard/formularios/builder/${response.data._id}`);
      } else {
        toast({
          title: "Erro",
          description: "Não foi possível criar o formulário",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Erro ao criar formulário:', error);
      toast({
        title: "Erro",
        description: "Não foi possível criar o formulário",
        variant: "destructive"
      });
    } finally {
      setCreatingDraft(false);
    }
  };

  const filteredForms = useMemo(() =>
    forms.filter(form =>
      form.title?.toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [forms, searchTerm]
  );

  const handleDelete = async () => {
    if (!formToDelete) return;
    try {
      const response = await formulariosService.deletar(formToDelete._id);
      if (!response.error) {
        setForms(forms.filter(f => f._id !== formToDelete._id));
        toast({
          title: "Sucesso",
          description: "Formulário deletado com sucesso"
        });
      }
    } catch (error) {
      console.error('Erro ao deletar:', error);
      toast({
        title: "Erro",
        description: "Não foi possível deletar o formulário",
        variant: "destructive"
      });
    } finally {
      setDeleteDialogOpen(false);
      setFormToDelete(null);
    }
  };

  const handleDuplicate = async (form) => {
    try {
      const response = await formulariosService.duplicar(form._id, user?.id);
      if (!response.error && response.data) {
        setForms([response.data, ...forms]);
        toast({
          title: "Sucesso",
          description: "Formulário duplicado com sucesso"
        });
      }
    } catch (error) {
      console.error('Erro ao duplicar:', error);
      toast({
        title: "Erro",
        description: "Não foi possível duplicar o formulário",
        variant: "destructive"
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div className="h-full overflow-y-auto">

      <PageContainerHeader
        titleSection={
          <div className="flex items-center gap-2 min-w-fit">
            <div className="p-2 bg-[#7C3AED]/10 rounded-lg">
              <Iconify icon="mdi:form-select" className="text-[#7C3AED]" width={20} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-[#D1D2D3]">Formulários</h3>
            </div>
          </div>
        }
        middleSection={
          <div className="relative group w-full md:w-auto">
            <Input
              placeholder="Pesquisar formulários..."
              value={searchTerm}
              className="w-full md:w-[200px] pl-3 pr-10 h-9 bg-gray-50 dark:bg-[#1A1D21] border-gray-200 dark:border-[#383838] focus:border-[#7C3AED] focus:ring-[#7C3AED]/20 hover:border-[#7C3AED]/50 transition-all text-sm rounded-lg dark:text-[#D1D2D3] dark:placeholder:text-[#ABABAD]"
              onChange={e => setSearchTerm(e.target.value)}
            />
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 gap-1">
              <span className="flex items-center text-gray-400 dark:text-[#ABABAD]">
                <Iconify icon="eva:search-outline" width={18} />
              </span>
            </div>
          </div>
        }
        rightSection={
          <div className="flex items-center gap-2 w-full md:w-auto justify-end">
            <Button
              className="h-9 w-9 p-0 rounded-lg border-dashed border-gray-300 dark:border-[#383838] text-gray-600 dark:text-[#ABABAD] hover:border-[#7C3AED] hover:text-[#7C3AED] hover:bg-[#7C3AED]/5"
              variant="outline"
              onClick={loadForms}
              title="Recarregar"
            >
              <Iconify icon="mdi:reload" width={18} />
            </Button>

            <Button
              className="h-9 bg-[#7C3AED] hover:bg-[#6D28D9] text-white shadow-sm shadow-[#7C3AED]/20 transition-all font-medium rounded-lg px-4 text-xs"
              onClick={handleNewForm}
              disabled={creatingDraft}
            >
              {creatingDraft ? (
                <Iconify icon="mdi:loading" className="mr-2 animate-spin" width={16} />
              ) : (
                <Iconify icon="eva:plus-fill" className="mr-2" width={16} />
              )}
              Novo Formulário
            </Button>
          </div>
        }
      />

      <div className="max-w-[95%] mx-auto">

        {loading ? (
          <Card className="rounded-2xl shadow-sm border border-gray-100 dark:border-[#383838] dark:bg-[#222529] overflow-hidden">
            <div className="flex flex-col items-center justify-center h-64 text-gray-400 dark:text-[#ABABAD]">
              <Iconify icon="mdi:loading" className="w-8 h-8 animate-spin mb-4 text-[#7C3AED]" />
              <p className="text-sm font-medium">Carregando formulários...</p>
            </div>
          </Card>
        ) : filteredForms.length === 0 ? (
          <Card className="rounded-2xl shadow-sm border border-gray-100 dark:border-[#383838] dark:bg-[#222529] overflow-hidden">
            <div className="flex flex-col items-center justify-center p-12 text-center">
              <div className="w-16 h-16 bg-gray-50 dark:bg-[#1A1D21] rounded-full flex items-center justify-center mb-4 text-gray-400 dark:text-[#ABABAD]">
                <Iconify icon="eva:inbox-outline" width={32} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-[#D1D2D3] mb-2">Nenhum formulário encontrado</h3>
              <p className="text-gray-500 dark:text-[#ABABAD] max-w-sm mb-6">
                Você ainda não criou nenhum formulário ou sua pesquisa não retornou resultados.
              </p>
              <Button variant="link" className="text-[#7C3AED] font-medium" onClick={handleNewForm}>
                Criar formulário agora
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="rounded-2xl shadow-sm border border-gray-100 dark:border-[#383838] dark:bg-[#222529] overflow-hidden">
            <div className="p-3">
              <Scrollbar sx={{ maxHeight: '70vh' }}>
                <Table>
                  <TableHeader className="bg-gray-50/80 dark:bg-[#1A1D21]/50 p-2">
                    <TableRow>
                      <TableHead>Título</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>ID Público</TableHead>
                      <TableHead>Perguntas</TableHead>
                      <TableHead>Data Criação</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredForms.map((form) => (
                      <TableRow key={form._id} className="group hover:bg-gray-50/50 dark:hover:bg-[#383838]/30 transition-colors">
                        <TableCell className="font-medium text-gray-900 dark:text-[#D1D2D3]">
                          {form.title || 'Sem título'}
                        </TableCell>
                        <TableCell>
                          {form.isPublished ? (
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-green-100 dark:bg-green-500/10 text-green-700 dark:text-green-400 rounded-full">
                              Publicado
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 rounded-full">
                              Rascunho
                            </span>
                          )}
                        </TableCell>
                        <TableCell>
                          {form.isStepByStep ? (
                            <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400 rounded-full">
                              Step-by-Step
                            </span>
                          ) : (
                            <span className="text-xs text-gray-500 dark:text-[#ABABAD]">Padrão</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {form.publicId ? (
                            <div className="flex items-center gap-1">
                              <code className="text-[11px] text-gray-600 dark:text-[#ABABAD] bg-gray-100 dark:bg-[#1A1D21] px-1.5 py-0.5 rounded font-mono">{form.publicId}</code>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-gray-400 dark:text-[#ABABAD] hover:text-[#7C3AED]"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const baseUrl = config.dev ? config.url_dev_links : config.url_prod_links;
                                  navigator.clipboard.writeText(`${baseUrl}/forms/${form.publicId}`);
                                  toast({ title: "Link copiado!" });
                                }}
                                title="Copiar link"
                              >
                                <Iconify icon="mdi:content-copy" width={14} />
                              </Button>
                            </div>
                          ) : (
                            <span className="text-[10px] text-gray-400 dark:text-[#ABABAD] italic">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-gray-600 dark:text-[#ABABAD]">{form.questions?.length || 0}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-gray-500 dark:text-[#ABABAD]">{formatDate(form.created_at)}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 rounded-lg text-gray-500 dark:text-[#ABABAD] hover:text-[#7C3AED] hover:bg-violet-50 dark:hover:bg-violet-500/10"
                              onClick={() => navigate(`/dashboard/formularios/builder/${form._id}`)}
                              title="Editar"
                            >
                              <Iconify icon="eva:edit-2-fill" width={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 rounded-lg text-gray-500 dark:text-[#ABABAD] hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-500/10"
                              onClick={() => navigate(`/dashboard/formularios/builder/${form._id}?tab=respostas`)}
                              title="Ver Respostas"
                            >
                              <Iconify icon="eva:message-circle-fill" width={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 rounded-lg text-gray-500 dark:text-[#ABABAD] hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-500/10"
                              onClick={() => handleDuplicate(form)}
                              title="Duplicar"
                            >
                              <Iconify icon="mdi:content-copy" width={16} />
                            </Button>
                            {form.isPublished && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0 rounded-lg text-gray-500 dark:text-[#ABABAD] hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-500/10"
                                onClick={() => {
                                  const baseUrl = config.dev ? config.url_dev_links : config.url_prod_links;
                                  window.open(`${baseUrl}/forms/${form.publicId}`, '_blank');
                                }}
                                title="Ver formulário"
                              >
                                <Iconify icon="eva:external-link-fill" width={16} />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0 rounded-lg text-gray-400 dark:text-[#ABABAD] hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10"
                              onClick={() => {
                                setFormToDelete(form);
                                setDeleteDialogOpen(true);
                              }}
                              title="Excluir"
                            >
                              <Iconify icon="eva:trash-2-fill" width={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Scrollbar>
            </div>

            <div className="text-center text-xs text-gray-400 dark:text-[#ABABAD] py-3 border-t border-gray-100 dark:border-[#383838]">
              Mostrando {filteredForms.length} de {forms.length} formulários
            </div>
          </Card>
        )}

      </div> {/* max-w-[95%] */}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl border-0 shadow-2xl dark:bg-[#222529]">
          <DialogHeader className="pt-6 px-6">
            <DialogTitle className="text-xl font-bold text-gray-900 dark:text-[#D1D2D3]">Confirmar exclusão</DialogTitle>
            <DialogDescription className="text-gray-500 dark:text-[#ABABAD] pt-2">
              Tem certeza que deseja excluir o formulário <span className="font-semibold text-gray-900 dark:text-[#D1D2D3]">"{formToDelete?.title}"</span>?
              <br />
              <span className="text-xs text-red-500 dark:text-red-400 mt-1 block">Esta ação é irreversível e todas as respostas serão perdidas.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="p-6 gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="rounded-xl border-gray-200 dark:border-[#383838] dark:text-[#D1D2D3]"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white rounded-xl shadow-lg shadow-red-600/20"
            >
              Excluir Formulário
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
